import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials } from 'sama-shared';
import { useLoginMutation } from 'sama-shared';
import { Button, Card, Typography, Form, Input, message, Row, Col } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  FireFilled,
  ThunderboltOutlined,
  ShopOutlined,
  LineChartOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { UserType } from 'sama-shared';
import { isDarkTheme } from '../../../components/ui/brasaTheme';
import type { RootState } from '../../../types/store';

const { Title, Text } = Typography;

/*
 * Intent:     aliado que entra apurado desde el móvil o la caja; el login debe
 *             vender velocidad y confianza en <3s: marca viva + acceso inmediato.
 * Hierarchy:  marca brasa (focal) → titular Sora → formulario (único camino).
 * Palette:    carbón cálido + orbes brasa/ámbar; el botón es gradiente con glow.
 * Depth:      card glass sobre fondo con orbes; borde whisper, sin sombras duras.
 * Typography: Sora 800 en marca · Inter en formulario · tabular nunca aquí.
 * Spacing:    base 4 · card 28px pad · radius 20/10 (concéntrico).
 */

const PERKS = [
  { icon: <ThunderboltOutlined />, title: 'Pedidos en vivo', desc: 'Cocina y delivery al segundo' },
  { icon: <ShopOutlined />, title: 'Multi-sucursal', desc: 'Todas tus sedes en un panel' },
  { icon: <LineChartOutlined />, title: 'Plata clara', desc: 'Ventas y retiros sin enredos' },
];

export const Login: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const [form] = Form.useForm();
  const currentTheme = useSelector((state: RootState) => (state as any).auth?.theme ?? 'dark');
  const isDark = isDarkTheme(currentTheme);
  const isOcean = currentTheme === 'ocean';

  const handleLogin = async (values: any) => {
    try {
      const response = await login(values).unwrap();
      const partner = response.partner
        ? {
            id: response.partner.id,
            name: response.partner.name,
            industry: response.partner.industry,
            exchangeRateCode: response.partner.exchangeRateCode,
          }
        : null;
      const allowedStores = response.allowedStores || [];
      const userType = response.type;
      const allowedPermissions = response.allowedPermissions || [];

      if (userType !== UserType.tenant) {
        message.error(
          'Acceso denegado: este portal es exclusivo para aliados comerciales/tenants.'
        );
        return;
      }

      dispatch(
        setCredentials({
          token: response.token,
          partner,
          allowedStores,
          type: userType,
          isSuperadmin: response.isSuperadmin,
          allowedPermissions,
        })
      );

      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      message.error(err?.data?.message || 'Error al iniciar sesión. Verifica tus credenciales.');
    }
  };

  const bg = isOcean ? '#0a1222' : isDark ? '#100c09' : '#fff3e6';
  const panelBg = isOcean ? '#0d1730' : isDark ? '#171310' : '#fffaf3';
  const line = isOcean
    ? 'rgba(147,197,253,0.14)'
    : isDark
      ? 'rgba(255,214,170,0.10)'
      : 'rgba(120,72,20,0.12)';
  const tx1 = isOcean ? '#eaf2ff' : isDark ? '#fff7ed' : '#221607';
  const tx2 = isOcean ? '#c3d4ee' : isDark ? '#e7d9c8' : '#5b4a3a';
  const tx3 = isOcean ? '#8ba1c1' : isDark ? '#a89885' : '#8a7663';
  const fieldBg = isOcean ? '#0a1222' : isDark ? '#100c09' : '#ffffff';
  const fieldBorder = isOcean
    ? 'rgba(147,197,253,0.20)'
    : isDark
      ? 'rgba(255,214,170,0.16)'
      : 'rgba(120,72,20,0.18)';

  return (
    <Row style={{ minHeight: '100vh', margin: 0, background: bg }}>
      {/* ——— Panel de marca (desktop) ——— */}
      <Col
        xs={0}
        lg={13}
        style={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '64px 72px',
          background: isOcean
            ? 'radial-gradient(1000px 500px at 20% 10%, rgba(255,90,0,0.12), transparent 60%), radial-gradient(800px 500px at 85% 90%, rgba(56,189,248,0.10), transparent 60%), #0a1222'
            : isDark
              ? 'radial-gradient(1000px 500px at 20% 10%, rgba(255,90,0,0.16), transparent 60%), radial-gradient(800px 500px at 85% 90%, rgba(255,176,32,0.10), transparent 60%), #100c09'
              : 'radial-gradient(1000px 500px at 20% 10%, rgba(255,90,0,0.12), transparent 60%), radial-gradient(800px 500px at 85% 90%, rgba(255,176,32,0.12), transparent 60%), #fff3e6',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            width: 420,
            height: 420,
            left: -140,
            top: -140,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,90,0,0.28), transparent 65%)',
            filter: 'blur(20px)',
            animation: 'sama-ember-drift 10s ease-in-out infinite',
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            width: 360,
            height: 360,
            right: -120,
            bottom: -120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,176,32,0.20), transparent 65%)',
            filter: 'blur(20px)',
            animation: 'sama-ember-drift 12s ease-in-out infinite reverse',
          }}
        />
        <div className="sama-stagger" style={{ position: 'relative', maxWidth: 520 }}>
          <div
            style={{
              ['--sama-i' as any]: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 28,
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #ff5a00, #ff7a1a 50%, #ffb020)',
                boxShadow:
                  '0 10px 30px -8px rgba(255,90,0,0.7), inset 0 1px 0 rgba(255,255,255,0.4)',
                color: '#fff',
                fontSize: 24,
              }}
            >
              <FireFilled />
            </div>
            <Text style={{ color: tx1, fontWeight: 800, fontSize: 17, letterSpacing: '0.08em' }}>
              SAMA
            </Text>
            <span className="sama-live-dot sama-live-dot--brasa" style={{ marginLeft: 2 }} />
          </div>
          <Title
            className="sama-display"
            style={{
              ['--sama-i' as any]: 1,
              color: tx1,
              fontSize: 46,
              lineHeight: 1.05,
              margin: '0 0 14px',
              fontWeight: 800,
            }}
          >
            Tu cocina, tu caja y tu delivery{' '}
            <span
              style={{
                background: 'linear-gradient(135deg,#ff5a00,#ffb020)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              en un solo fuego.
            </span>
          </Title>
          <Text
            style={{
              ['--sama-i' as any]: 2,
              color: tx2,
              fontSize: 15,
              display: 'block',
              marginBottom: 36,
              maxWidth: 440,
            }}
          >
            El centro de comando del aliado SAMA: pedidos en vivo, catálogo, finanzas y equipo.
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PERKS.map((p, i) => (
              <div
                key={p.title}
                style={{
                  ['--sama-i' as any]: 3 + i,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: isOcean
                    ? 'rgba(147,197,253,0.06)'
                    : isDark
                      ? 'rgba(255,214,170,0.045)'
                      : 'rgba(255,255,255,0.7)',
                  border: `1px solid ${line}`,
                  borderRadius: 14,
                  padding: '12px 16px',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(135deg, rgba(255,90,0,0.25), rgba(255,176,32,0.14))',
                    boxShadow: 'inset 0 0 0 1px rgba(255,122,26,0.3)',
                    color: '#ff8a2a',
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                >
                  {p.icon}
                </div>
                <div>
                  <Text strong style={{ color: tx1, fontSize: 14, display: 'block' }}>
                    {p.title}
                  </Text>
                  <Text style={{ color: tx3, fontSize: 12.5 }}>{p.desc}</Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Col>

      {/* ——— Formulario ——— */}
      <Col
        xs={24}
        lg={11}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 20px',
          background: panelBg,
        }}
      >
        <Card
          variant="borderless"
          className="sama-enter"
          style={{
            width: '100%',
            maxWidth: 420,
            borderRadius: 20,
            background: isOcean ? '#101a30' : isDark ? '#1e1814' : '#ffffff',
            border: `1px solid ${line}`,
            boxShadow: isOcean
              ? '0 24px 70px -24px rgba(255,90,0,0.35), 0 8px 30px rgba(2,8,23,0.6)'
              : isDark
                ? '0 24px 70px -24px rgba(255,90,0,0.35), 0 8px 30px rgba(0,0,0,0.5)'
                : '0 24px 60px -28px rgba(255,90,0,0.3), 0 8px 24px rgba(120,72,20,0.08)',
            padding: 8,
          }}
          styles={{ body: { padding: 28 } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #ff5a00, #ffb020)',
                boxShadow:
                  '0 8px 22px -6px rgba(255,90,0,0.65), inset 0 1px 0 rgba(255,255,255,0.35)',
                color: '#fff',
                fontSize: 20,
              }}
            >
              <FireFilled />
            </div>
            <div>
              <Text
                strong
                className="sama-display"
                style={{ color: tx1, fontSize: 16, display: 'block', lineHeight: 1.2 }}
              >
                SAMA Aliados
              </Text>
              <Text style={{ color: tx3, fontSize: 12 }}>Centro de comando</Text>
            </div>
          </div>

          <Title
            level={3}
            className="sama-display"
            style={{ color: tx1, margin: '14px 0 4px', fontSize: 23 }}
          >
            Bienvenido de vuelta
          </Title>
          <Text style={{ color: tx3, fontSize: 13, display: 'block', marginBottom: 24 }}>
            Tus pedidos te están esperando en el pase.
          </Text>

          <Form
            form={form}
            name="login"
            onFinish={handleLogin}
            size="large"
            autoComplete="on"
            layout="vertical"
          >
            <Form.Item
              name="email"
              label={
                <span style={{ color: tx2, fontSize: 13, fontWeight: 600 }}>
                  Correo electrónico
                </span>
              }
              rules={[{ required: true, message: 'Por favor ingresa tu correo!' }]}
              style={{ marginBottom: 14 }}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#ff7a1a' }} />}
                placeholder="tucorreo@negocio.com"
                autoComplete="email"
                type="email"
                style={{
                  background: fieldBg,
                  borderColor: fieldBorder,
                  color: tx1,
                  borderRadius: 10,
                  height: 46,
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={{ color: tx2, fontSize: 13, fontWeight: 600 }}>Contraseña</span>}
              rules={[{ required: true, message: 'Por favor ingresa tu contraseña!' }]}
              style={{ marginBottom: 22 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#ff7a1a' }} />}
                placeholder="••••••••"
                autoComplete="current-password"
                style={{
                  background: fieldBg,
                  borderColor: fieldBorder,
                  color: tx1,
                  borderRadius: 10,
                  height: 46,
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
                icon={!isLoading ? <ArrowRightOutlined /> : undefined}
                iconPosition="end"
                style={{ height: 48, fontSize: 15, borderRadius: 10 }}
              >
                Ingresar al panel
              </Button>
            </Form.Item>
          </Form>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 18,
              justifyContent: 'center',
            }}
          >
            <span className="sama-live-dot" />
            <Text style={{ color: tx3, fontSize: 12 }}>
              Operativa en vivo · Soporte aliado 24/7
            </Text>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default Login;
